import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, List, ListItem, ListItemIcon, ListItemText,
  AppBar, Toolbar, Typography, IconButton, Avatar, Menu,
  MenuItem, Chip, Badge, Popover, List as MuiList, ListItem as MuiListItem,
  Divider, useTheme, useMediaQuery
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import ViewListIcon from '@mui/icons-material/ViewList';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SettingsIcon from '@mui/icons-material/Settings';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import ChatIcon from '@mui/icons-material/Chat';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import ChatPanel from './ChatPanel';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const drawerWidth = 220;

const TIPO_COLORES = {
  info:    { bg: '#0d47a1', text: '#bbdefb' },
  warning: { bg: '#e65100', text: '#fff3e0' },
  success: { bg: '#1b5e20', text: '#c8e6c9' },
  error:   { bg: '#b71c1c', text: '#ffcdd2' },
};

export default function Layout({ children, usuario, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [avisos, setAvisos] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const lastSeenIdRef = React.useRef(0);
  const [enRecepcion, setEnRecepcion] = useState([]);
  const [notifAnchor, setNotifAnchor] = useState(null);

  const tieneChat = ['admin', 'recepcionista', 'medico'].includes(usuario.rol);

  // En mobile el drawer arranca cerrado
  useEffect(() => {
    if (isMobile) setOpen(false);
  }, [isMobile]);

  useEffect(() => {
    axios.get(`${API}/api/avisos`)
      .then(r => setAvisos(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (usuario.rol !== 'medico') return;
    const poll = () => {
      axios.get(`${API}/api/turnos/en-recepcion`)
        .then(r => setEnRecepcion(r.data))
        .catch(() => {});
    };
    poll();
    const iv = setInterval(poll, 10000);
    return () => clearInterval(iv);
  }, [usuario.rol]);

  useEffect(() => {
    if (!tieneChat || chatOpen) return;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/api/chat`, { params: { desde_id: lastSeenIdRef.current } });
        if (r.data.length > 0) {
          lastSeenIdRef.current = r.data[r.data.length - 1].id;
          setUnread(prev => prev + r.data.length);
        }
      } catch {}
    };
    if (lastSeenIdRef.current === 0) {
      axios.get(`${API}/api/chat`).then(r => {
        if (r.data.length > 0) lastSeenIdRef.current = r.data[r.data.length - 1].id;
      }).catch(() => {});
    }
    const interval = setInterval(poll, 15000);
    return () => clearInterval(interval);
  }, [chatOpen, tieneChat]);

  const menu = [
    { text: 'Mis turnos',   icon: <DashboardIcon />,        path: '/panel-paciente', roles: ['paciente'] },
    { text: 'Mis datos',    icon: <PersonIcon />,            path: '/mis-datos',      roles: ['paciente'] },
    { text: 'Calendario',   icon: <CalendarMonthIcon />,     path: '/calendario',     roles: ['admin', 'recepcionista', 'medico'] },
    { text: 'Turnos',       icon: <ViewListIcon />,          path: '/turnos',         roles: ['admin', 'recepcionista', 'medico'] },
    { text: 'Profesionales y agenda', icon: <LocalHospitalIcon />, path: '/medicos',  roles: ['admin'] },
    { text: 'Especialidades', icon: <MedicalServicesIcon />, path: '/especialidades', roles: ['admin'] },
    { text: 'Obras sociales', icon: <LocalHospitalIcon />,   path: '/obras-sociales', roles: ['admin'] },
    { text: 'Pacientes',    icon: <PeopleIcon />,            path: '/pacientes',      roles: ['admin', 'recepcionista'] },
    { text: 'Búsqueda HC',  icon: <FindInPageIcon />,        path: '/busqueda-hc',    roles: ['admin', 'medico'] },
    { text: 'Usuarios',     icon: <ManageAccountsIcon />,    path: '/staff',          roles: ['admin'] },
    { text: 'Configuración', icon: <SettingsIcon />,         path: '/configuracion',  roles: ['admin'] },
  ].filter(item => item.roles.includes(usuario.rol));

  function handleMenuClick(path) {
    navigate(path);
    if (isMobile) setOpen(false); // Cerrar drawer en mobile al navegar
  }

  function colorRol(rol) {
    const colores = { admin: 'error', medico: 'info', recepcionista: 'warning', paciente: 'success' };
    return colores[rol] || 'default';
  }

  const topOffset = avisos.length > 0 ? '96px' : '64px';

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'var(--c-fondo)' }}>

      {/* ── AppBar ── */}
      <AppBar position="fixed" sx={{ zIndex: 1201, bgcolor: 'var(--c-superficie)' }}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <IconButton color="inherit" onClick={() => setOpen(!open)} sx={{ mr: 1 }}>
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" sx={{
            color: '#e0e0e0', fontWeight: 700, flexGrow: 1,
            fontSize: { xs: 15, sm: 20 },
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            Sistema Médico
          </Typography>

          {/* Chip de rol — oculto en mobile muy pequeño */}
          <Chip
            label={usuario.rol}
            color={colorRol(usuario.rol)}
            size="small"
            sx={{ mr: 1, display: { xs: 'none', sm: 'flex' } }}
          />

          {/* Nombre del usuario — oculto en mobile */}
          <Typography sx={{ color: '#b0b0b0', mr: 1, fontSize: 14, display: { xs: 'none', md: 'block' } }}>
            {usuario.nombre}
          </Typography>

          {/* Notificaciones en recepción (solo médico) */}
          {usuario.rol === 'medico' && (
            <IconButton
              onClick={e => setNotifAnchor(e.currentTarget)}
              sx={{ mr: 0.5, color: enRecepcion.length > 0 ? '#ffb74d' : '#b0b0b0' }}
            >
              <Badge badgeContent={enRecepcion.length} color="warning" invisible={enRecepcion.length === 0}>
                <NotificationsIcon />
              </Badge>
            </IconButton>
          )}

          <Popover
            open={Boolean(notifAnchor)}
            anchorEl={notifAnchor}
            onClose={() => setNotifAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: {
              bgcolor: 'var(--c-superficie)', color: '#e0e0e0',
              width: { xs: '90vw', sm: 320 },
              border: '1px solid var(--c-primario)',
            }}}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid var(--c-primario)' }}>
              <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#ffb74d' }}>
                Pacientes en recepción
              </Typography>
            </Box>
            {enRecepcion.length === 0 ? (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography sx={{ fontSize: 13, color: '#666' }}>Sin pacientes esperando.</Typography>
              </Box>
            ) : (
              <MuiList dense disablePadding>
                {enRecepcion.map((t, i) => (
                  <React.Fragment key={t.id}>
                    {i > 0 && <Divider sx={{ borderColor: 'var(--c-primario)' }} />}
                    <MuiListItem sx={{ py: 1, px: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Box
                        onClick={() => {
                          if (t.paciente_id) {
                            navigate(`/historial/${t.paciente_id}?nueva=1&turno=${t.id}`);
                            setNotifAnchor(null);
                          }
                        }}
                        sx={{ flex: 1, cursor: t.paciente_id ? 'pointer' : 'default', borderRadius: 1, px: 1, py: 0.5,
                          '&:hover': t.paciente_id ? { bgcolor: 'rgba(255,255,255,0.06)' } : {} }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: t.paciente_id ? 'var(--c-acento)' : '#e0e0e0' }}>
                          {t.paciente_nombre || '—'}
                          {t.paciente_id && <Typography component="span" sx={{ fontSize: 10, ml: 0.5, color: '#90a4ae' }}>→ HC</Typography>}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#90a4ae' }}>
                          {t.paciente_dni ? `DNI ${t.paciente_dni} · ` : ''}
                          {t.hora_inicio ? String(t.hora_inicio).substring(0, 5) : ''}
                          {t.medico_nombre ? ` · ${t.medico_nombre}` : ''}
                        </Typography>
                      </Box>
                      <IconButton size="small" title="Marcar como atendido"
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/api/turnos/${t.id}`, { estado: 'atendido' });
                            setEnRecepcion(prev => prev.filter(x => x.id !== t.id));
                          } catch {}
                        }}
                        sx={{ bgcolor: 'rgba(102,187,106,0.15)', color: '#66bb6a', flexShrink: 0, '&:hover': { bgcolor: 'rgba(102,187,106,0.3)' } }}
                      >
                        <Typography sx={{ fontSize: 10, fontWeight: 700, px: 0.5 }}>✓</Typography>
                      </IconButton>
                    </MuiListItem>
                  </React.Fragment>
                ))}
              </MuiList>
            )}
          </Popover>

          {/* Chat */}
          {tieneChat && (
            <IconButton
              onClick={() => { setChatOpen(o => !o); setUnread(0); }}
              sx={{ mr: 0.5, color: chatOpen ? 'var(--c-acento)' : '#b0b0b0',
                bgcolor: chatOpen ? 'rgba(144,202,249,0.1)' : 'transparent',
                '&:hover': { color: 'var(--c-acento)' } }}
            >
              <Badge badgeContent={unread > 9 ? '9+' : unread} color="error" invisible={unread === 0}
                sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16, p: '0 3px' } }}>
                <ChatIcon />
              </Badge>
            </IconButton>
          )}

          <Avatar
            sx={{ bgcolor: 'var(--c-primario)', cursor: 'pointer', width: 32, height: 32, fontSize: 14 }}
            onClick={e => setAnchorEl(e.currentTarget)}
          >
            {usuario.nombre?.charAt(0)}
          </Avatar>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
            PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
            <MenuItem disabled sx={{ fontSize: 13, opacity: 0.7 }}>{usuario.nombre}</MenuItem>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
            <MenuItem onClick={onLogout} sx={{ color: '#f48fb1' }}>Cerrar sesión</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* ── Banner de avisos ── */}
      {avisos.length > 0 && (
        <Box sx={{
          position: 'fixed', top: { xs: 56, sm: 64 }, left: 0, right: 0, zIndex: 1200,
          bgcolor: TIPO_COLORES[avisos[0].tipo]?.bg || TIPO_COLORES.info.bg,
          color: TIPO_COLORES[avisos[0].tipo]?.text || TIPO_COLORES.info.text,
          overflow: 'hidden', height: 32, display: 'flex', alignItems: 'center',
        }}>
          <style>{`@keyframes marquee { 0% { transform: translateX(100vw); } 100% { transform: translateX(-100%); } }`}</style>
          <Box sx={{
            display: 'inline-block', whiteSpace: 'nowrap',
            animation: `marquee ${Math.max(20, avisos.map(a => a.texto.length).reduce((s, l) => s + l, 0) * 0.08)}s linear infinite`,
            fontSize: 13, fontWeight: 500, letterSpacing: 0.3,
          }}>
            {avisos.map((a, i) => (
              <span key={a.id}>{a.texto}{i < avisos.length - 1 && <span style={{ margin: '0 40px', opacity: 0.5 }}>◆</span>}</span>
            ))}
            <span style={{ margin: '0 40px', opacity: 0.5 }}>◆</span>
            {avisos.map((a, i) => (
              <span key={`r-${a.id}`}>{a.texto}{i < avisos.length - 1 && <span style={{ margin: '0 40px', opacity: 0.5 }}>◆</span>}</span>
            ))}
          </Box>
        </Box>
      )}

      {/* ── Drawer ── */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidth,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            bgcolor: 'var(--c-superficie)',
            color: '#e0e0e0',
            mt: isMobile ? 0 : (avisos.length > 0 ? '96px' : '64px'),
            transition: 'margin-top 0.2s',
          }
        }}
      >
        {/* En mobile mostramos un header con botón cerrar */}
        {isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: '1px solid var(--c-primario)' }}>
            <Typography sx={{ fontWeight: 700, color: 'var(--c-acento)', fontSize: 15 }}>Menú</Typography>
            <IconButton onClick={() => setOpen(false)} size="small" sx={{ color: '#b0b0b0' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        <List>
          {menu.map(item => (
            <ListItem button key={item.text} onClick={() => handleMenuClick(item.path)}
              sx={{
                bgcolor: location.pathname === item.path ? 'var(--c-primario)' : 'transparent',
                '&:hover': { bgcolor: 'var(--c-primario)' },
                borderRadius: 1, mx: 1, my: 0.5,
              }}
            >
              <ListItemIcon sx={{ color: 'var(--c-acento)', minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* ── Panel chat — fullscreen en mobile ── */}
      {tieneChat && chatOpen && (
        <Box sx={{
          position: 'fixed',
          top: isMobile ? 0 : topOffset,
          right: isMobile ? 0 : 24,
          left: isMobile ? 0 : 'auto',
          bottom: isMobile ? 0 : 'auto',
          width: isMobile ? '100%' : 320,
          height: isMobile ? '100%' : `calc(100vh - ${topOffset === '96px' ? 120 : 88}px)`,
          bgcolor: 'var(--c-fondo)',
          border: '1px solid var(--c-primario)',
          borderRadius: isMobile ? 0 : 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          zIndex: isMobile ? 1300 : 1100,
        }}>
          <ChatPanel open={chatOpen} onClose={() => setChatOpen(false)} />
        </Box>
      )}

      {/* ── Contenido principal ── */}
      <Box component="main" sx={{
        flexGrow: 1,
        p: { xs: 1.5, sm: 3 },
        mt: avisos.length > 0 ? { xs: '88px', sm: '96px' } : { xs: '56px', sm: '64px' },
        ml: !isMobile && open ? `${drawerWidth}px` : 0,
        transition: 'margin 0.3s',
        color: '#e0e0e0',
        minWidth: 0, // Evita overflow en flex
      }}>
        {children}
      </Box>
    </Box>
  );
}
